import React, { useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  IconButton,
  InputAdornment,
  FormControlLabel,
  Switch,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import SidebarMenu from "./AdminPanel/SidebarMenu";
import TopBar from "./AdminPanel/TopBar";
import { useNavigate } from "react-router-dom";

const BANCOS_ECUADOR = [
  "BANCO PICHINCHA",
  "BANCO GUAYAQUIL",
  "BANCO DEL PACÍFICO",
  "PRODUBANCO",
  "BANCO INTERNACIONAL",
  "BANCO BOLIVARIANO",
  "BANCO SOLIDARIO",
  "BANCO DE LOJA",
  "BANCO GENERAL RUMIÑAHUI",
];

const tdLabelSx = {
  width: 220,
  bgcolor: "#f3f3f3",
  borderRight: "1px solid #e0e0e0",
  color: "#333",
  fontWeight: 600,
  verticalAlign: "top",
};

const tdFieldSx = { p: 1, verticalAlign: "middle" };
const inputSx = { width: "50%" };

const Bank = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(false);

  const [form, setForm] = useState({
    nombre: "",
    numero: "",
    tipo: "AHORROS",
    activa: true,
    cuentaContableId: "",
    cuentaContableText: "",
    banco: "",
    ciudad: "",
  });

  const [pickerOpen, setPickerOpen] = useState(false);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [accounts, setAccounts] = useState([]);

  const [resultModal, setResultModal] = useState({
    open: false,
    ok: false,
    title: "",
    message: "",
  });

  const getAccessToken = () => localStorage.getItem("access") || "";

  const buildAuthHeaders = (extra = {}) => {
    const token = getAccessToken();
    const headers = { ...extra };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    return headers;
  };

  const closeResultModal = () => {
    setResultModal((p) => ({ ...p, open: false }));
    if (resultModal.ok) navigate(-1);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const normalizeAccounts = (payload) => {
    const arr = payload?.cuentas || [];
    return arr
      .map((x) => ({
        id: String(x.id),
        codigo: String(x.codigo),
        nombre: String(x.nombre),
        tipo: String(x.tipo || ""),
      }))
      .filter((x) => x.id && x.codigo && x.nombre);
  };

  const fetchAccounts = async () => {
    if (accountsLoading) return;
    setAccountsLoading(true);
    try {
      const r = await fetch("/cuentas/bancos/cuentas/", {
        method: "GET",
        headers: buildAuthHeaders(),
      });

      if (r.status === 401 || r.status === 403) {
        navigate("/login");
        setAccountsLoading(false);
        return;
      }

      const ct = (r.headers.get("content-type") || "").toLowerCase();
      if (!ct.includes("application/json")) throw new Error("No se pudieron cargar las cuentas");
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j?.error || "No se pudieron cargar las cuentas");
      setAccounts(normalizeAccounts(j));
    } catch (e) {
      setAccounts([]);
      setResultModal({
        open: true,
        ok: false,
        title: "No se pudieron cargar cuentas",
        message: e?.message || "No se pudieron cargar las cuentas",
      });
    } finally {
      setAccountsLoading(false);
    }
  };

  const openPicker = async () => {
    setPickerOpen(true);
    if (!accounts.length) await fetchAccounts();
  };

  const selectAccount = (a) => {
    setForm((p) => ({
      ...p,
      cuentaContableId: a.id,
      cuentaContableText: `${a.codigo} - ${a.nombre}`,
    }));
    setPickerOpen(false);
  };

  const typeLabel = useMemo(() => {
    if (form.tipo === "CORRIENTE") return "Cuenta Corriente";
    return "Cuenta de Ahorros";
  }, [form.tipo]);

  const guardar = async (e) => {
    e?.preventDefault?.();
    if (saving) return;

    const payload = {
      nombre: String(form.nombre || "").trim(),
      numero: String(form.numero || "").trim(),
      tipo_cuenta: form.tipo,
      activa: !!form.activa,
      banco: String(form.banco || "").trim(),
      ciudad: String(form.ciudad || "").trim(),
      cuenta_contable_id: form.cuentaContableId,
      cuenta_contable_texto: form.cuentaContableText,
    };

    setSaving(true);
    try {
      const r = await fetch("/cuentas/bancos/crear/", {
        method: "POST",
        headers: buildAuthHeaders({
          "Content-Type": "application/json",
        }),
        body: JSON.stringify(payload),
      });

      if (r.status === 401 || r.status === 403) {
        navigate("/login");
        setSaving(false);
        return;
      }

      const ct = (r.headers.get("content-type") || "").toLowerCase();
      const j = ct.includes("application/json") ? await r.json().catch(() => ({})) : {};
      if (!r.ok) throw new Error(typeof j?.error === "string" ? j.error : "No se pudo guardar");

      setResultModal({
        open: true,
        ok: true,
        title: "Guardado correctamente",
        message: "La cuenta bancaria se guardó correctamente.",
      });

      setForm({
        nombre: "",
        numero: "",
        tipo: "AHORROS",
        activa: true,
        cuentaContableId: "",
        cuentaContableText: "",
        banco: "",
        ciudad: "",
      });
    } catch (e2) {
      setResultModal({
        open: true,
        ok: false,
        title: "No se pudo guardar",
        message: e2?.message || "No se pudo guardar la cuenta bancaria.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f5f6fa" }}>
      <SidebarMenu
        open={open}
        submenuOpen={submenuOpen}
        setSubmenuOpen={setSubmenuOpen}
        navigate={navigate}
      />

      <Box sx={{ flexGrow: 1 }}>
        <TopBar open={open} setOpen={setOpen} />

        <Box sx={{ width: "95%", mx: "auto", mt: 3, mb: 5 }}>
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Typography sx={{ fontWeight: 800, color: "#1565C0", fontSize: "1.1rem" }}>
              Cuenta Bancaria
            </Typography>

            <Typography sx={{ color: "#556", mt: 0.5, mb: 3 }}>
              Los campos con (*) son obligatorios.
            </Typography>

            <Box component="form" onSubmit={guardar}>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell sx={tdLabelSx}>Nombre:</TableCell>
                    <TableCell sx={tdFieldSx}>
                      <TextField name="nombre" value={form.nombre} onChange={handleChange} required sx={inputSx} />
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell sx={tdLabelSx}>Número de cuenta:</TableCell>
                    <TableCell sx={tdFieldSx}>
                      <TextField name="numero" value={form.numero} onChange={handleChange} required sx={inputSx} />
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell sx={tdLabelSx}>Tipo de cuenta:</TableCell>
                    <TableCell sx={tdFieldSx}>
                      <TextField select name="tipo" value={form.tipo} onChange={handleChange} required sx={inputSx}>
                        <MenuItem value="AHORROS">Cuenta de Ahorros</MenuItem>
                        <MenuItem value="CORRIENTE">Cuenta Corriente</MenuItem>
                      </TextField>
                      <Typography sx={{ mt: 0.6, color: "#667", fontSize: "0.85rem" }}>
                        Seleccionado: {typeLabel}
                      </Typography>
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell sx={tdLabelSx}>Cuenta contable:</TableCell>
                    <TableCell sx={tdFieldSx}>
                      <TextField
                        value={form.cuentaContableText}
                        required
                        sx={inputSx}
                        InputProps={{
                          readOnly: true,
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton onClick={openPicker}>
                                <SearchIcon />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                      <input type="hidden" value={form.cuentaContableId} required readOnly />
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell sx={tdLabelSx}>Banco:</TableCell>
                    <TableCell sx={tdFieldSx}>
                      <TextField select name="banco" value={form.banco} onChange={handleChange} required sx={inputSx}>
                        {BANCOS_ECUADOR.map((b) => (
                          <MenuItem key={b} value={b}>
                            {b}
                          </MenuItem>
                        ))}
                      </TextField>
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell sx={tdLabelSx}>Ciudad:</TableCell>
                    <TableCell sx={tdFieldSx}>
                      <TextField name="ciudad" value={form.ciudad} onChange={handleChange} required sx={inputSx} />
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell sx={tdLabelSx}>Estado:</TableCell>
                    <TableCell sx={tdFieldSx}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={form.activa}
                            onChange={(e) => setForm((p) => ({ ...p, activa: e.target.checked }))}
                          />
                        }
                        label={form.activa ? "Cuenta activa" : "Cuenta inactiva"}
                      />
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell sx={tdLabelSx}></TableCell>
                    <TableCell sx={tdFieldSx}>
                      <Button
                        type="submit"
                        variant="contained"
                        sx={{
                          bgcolor: "#1565C0",
                          "&:hover": { bgcolor: "#0D47A1" },
                          textTransform: "none",
                        }}
                        disabled={saving}
                      >
                        {saving ? "Guardando..." : "Guardar cuenta bancaria"}
                      </Button>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          </Paper>
        </Box>
      </Box>

      <Modal open={pickerOpen} onClose={() => setPickerOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 720,
            maxWidth: "92vw",
            bgcolor: "#fff",
            borderRadius: 2,
            p: 3,
          }}
        >
          <Typography sx={{ fontWeight: 800, color: "#1565C0", mb: 2 }}>
            Seleccionar cuenta contable
          </Typography>

          <TableContainer sx={{ border: "1px solid #e6e6e6", borderRadius: 1, maxHeight: 420 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ backgroundColor: "#f2f2f2", fontWeight: 800, width: 160 }}>
                    Código
                  </TableCell>
                  <TableCell sx={{ backgroundColor: "#f2f2f2", fontWeight: 800 }}>
                    Cuenta
                  </TableCell>
                  <TableCell sx={{ backgroundColor: "#f2f2f2", fontWeight: 800, width: 140 }}>
                    Tipo
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {accountsLoading ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 6 }}>
                      <CircularProgress size={26} />
                    </TableCell>
                  </TableRow>
                ) : accounts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} sx={{ py: 6 }}>
                      <Box sx={{ textAlign: "center" }}>
                        <Typography sx={{ fontWeight: 800, color: "#222" }}>
                          No hay cuentas para mostrar
                        </Typography>
                        <Typography sx={{ color: "#666", mt: 0.5 }}>
                          Presiona “Cargar cuentas” para intentar de nuevo.
                        </Typography>
                        <Button
                          variant="outlined"
                          sx={{
                            mt: 2,
                            textTransform: "none",
                            borderColor: "#1565C0",
                            color: "#1565C0",
                            "&:hover": { borderColor: "#0D47A1", color: "#0D47A1" },
                          }}
                          onClick={fetchAccounts}
                          disabled={accountsLoading}
                        >
                          {accountsLoading ? "Cargando..." : "Cargar cuentas"}
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  accounts.map((a) => (
                    <TableRow key={a.id} hover onClick={() => selectAccount(a)} sx={{ cursor: "pointer" }}>
                      <TableCell>{a.codigo}</TableCell>
                      <TableCell>{a.nombre}</TableCell>
                      <TableCell>{a.tipo || "-"}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button variant="outlined" onClick={() => setPickerOpen(false)} sx={{ textTransform: "none" }}>
              Cerrar
            </Button>
          </Box>
        </Box>
      </Modal>

      <Dialog open={resultModal.open} onClose={closeResultModal} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, color: resultModal.ok ? "#1565C0" : "#d32f2f" }}>
          {resultModal.title}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 1, color: "#444" }}>{resultModal.message}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={closeResultModal}
            variant="contained"
            sx={{
              bgcolor: resultModal.ok ? "#1565C0" : "#d32f2f",
              "&:hover": { bgcolor: resultModal.ok ? "#0D47A1" : "#b71c1c" },
              textTransform: "none",
            }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Bank;